import type { QueryParams, TenantConfig } from '@buildex/module-contract';
import { ApiError } from './errors';
import { getCurrentTenantSlug } from '../tenant/current-tenant';
import type { Session } from '../session/types';
import { mockAppApiRequest, NOT_APP_API } from './mock-app-api';

/**
 * Мок эндпоинтов shell'а (конфиг тенанта, логин/логаут).
 *
 * У App API (app-api-service, FRONTEND_GUIDE) таких эндпоинтов нет: аутентификация —
 * Keycloak/OIDC либо заголовок X-Tenant-Id, список модулей/брендинг бэк не отдаёт.
 * Поэтому эти пути мокируются даже при VITE_API_MOCK=0, пока бэк не даст им замену.
 */

/** Пути shell'а, которых нет в App API — всегда обслуживаются моком */
export function isShellMockPath(path: string): boolean {
  return path === '/tenant/config' || path.startsWith('/auth/');
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function mockTenantConfig(): TenantConfig {
  const slug = getCurrentTenantSlug() ?? 'demo';
  return {
    tenant: {
      id: 'ten-0001',
      slug,
      name: slug.toUpperCase(),
    },
    modules: ['pricing', 'dco'],
    branding: {
      logoUrl: '',
      primaryColor: '#0b7285',
      colorScheme: 'light',
      radius: 'md',
    },
    featureFlags: {
      'sco.enabled': false,
      'planner.enabled': false,
      'marker.enabled': false,
    },
  };
}

function mockLogin(body: unknown): Session {
  const { email } = (body ?? {}) as { email?: string };
  if (!email) {
    throw new ApiError(400, 'email is required');
  }
  return {
    userId: 'usr-0001',
    email,
    name: email.split('@')[0] ?? email,
    token: `mock-token-${Date.now()}`,
    permissions: ['*'],
  };
}

export async function mockRequest<T>(
  method: string,
  path: string,
  body?: unknown,
  query?: QueryParams,
): Promise<T> {
  await delay(250);

  if (method === 'GET' && path === '/tenant/config') {
    return mockTenantConfig() as T;
  }
  if (method === 'POST' && path === '/auth/login') {
    return mockLogin(body) as T;
  }
  if (method === 'POST' && path === '/auth/logout') {
    return undefined as T;
  }

  // Эндпоинты App API (шахматка, approvals, конфиг, ручная цена)
  const appApiResult = mockAppApiRequest(method, path, body, query);
  if (appApiResult !== NOT_APP_API) {
    return appApiResult as T;
  }

  throw new ApiError(404, `Mock: не замокан ${method} ${path}`);
}
